import { MetaInfoState } from './metaInfoTypes';
import { ActiveViews } from 'streams/views/mainContent/activeView$';
import {
  SetDescriptionPayload,
  SetInitialMetaInfoPayload,
  SetNamePayload,
} from './metaInfoActions';
import { MetaInfo } from 'streams/views/mainContent/types';
import { ViewIds } from '@ardoq/api-types';
import type { ModeViews } from 'views/modeViews$';
import type { ViewpointModeViewKey } from 'views/environmentals/viewSettings/types';
import {
  isViewHiddenByBlockDiagramSwap,
  toStoredViewpointViewId,
} from 'views/environmentals/getViewpointModeSupportedViews';
import { SelectedViewState } from '../selectedView/types';
import { findIndex } from 'lodash';
import { LabelFormattingInfo } from '@ardoq/data-model';
import { PerspectiveGroupingRule } from '@ardoq/perspectives';
import { getViewSelectorWarningCode } from './getViewSelectorWarningMessage';

const initialState: MetaInfoState = {
  name: null,
  description: null,
  viewName: ViewIds.NONE,
  viewSelectWarning: undefined,
  folderId: null,
  viewOptions: [],
  initialState: null,
  canSaveViewpoint: false,
  stateAsSavableAttributes: null,
};

export const getInitialMetaInfoState = () => structuredClone(initialState);

type ViewOption = { value: ViewIds };

const viewsToViewOptions = (views: MetaInfo[]): ViewOption[] => {
  const viewOptions = views
    .map(({ id }) => ({
      value: id,
    }))
    .filter(({ value }) => !isViewHiddenByBlockDiagramSwap(value));

  const index = findIndex(viewOptions, {
    value: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
  });
  if (index > 0) {
    const [modernizedBlockDiagram] = viewOptions.splice(index, 1);
    viewOptions.unshift(modernizedBlockDiagram);
  }

  return viewOptions;
};

type MetaInfoDependencies = {
  activeViews: ActiveViews;
  viewpointModeViews: ModeViews<ViewpointModeViewKey>;
};

const processDependencies = (
  state: MetaInfoState,
  {
    activeViews: { mainViewId },
    viewpointModeViews: { views },
  }: MetaInfoDependencies
): MetaInfoState => {
  if (state.initialState === null) {
    const viewOptions = viewsToViewOptions(views);
    return {
      ...state,
      viewOptions,
    };
  }

  const viewOptions = viewsToViewOptions(views);

  return {
    ...state,
    viewOptions,
    viewName: toSelectedViewName({
      viewOptions,
      mainViewId,
      initialViewName: state.initialState.viewName,
    }),
  };
};

type ToSelectedViewNameArgs = {
  viewOptions: ViewOption[];
  mainViewId: ViewIds;
  initialViewName: ViewIds;
};

/**
 * The view style to show when the active view changes under an open modal. The
 * style the modal *opens* with comes from `setInitialState` and the
 * `setSelectedView` that `initViewpointBuilder` dispatches, both of which run
 * after this reducer's first emission.
 *
 * The modal's options are the viewpoint-mode ones whatever the app is rendering,
 * so an active view it does not offer — anything workspace-mode, or the Flexible
 * Block Diagram while it stands in for the Block Diagram — falls back to the
 * style the viewpoint itself holds rather than to none.
 */
export const toSelectedViewName = ({
  viewOptions,
  mainViewId,
  initialViewName,
}: ToSelectedViewNameArgs): ViewIds =>
  getSelectedView(viewOptions, toStoredViewpointViewId(mainViewId))?.value ??
  getSelectedView(viewOptions, initialViewName)?.value ??
  ViewIds.NONE;

const getSelectedView = (viewOptions: ViewOption[], viewId: ViewIds) =>
  viewOptions.find(({ value }) => value === viewId) ?? null;

const setTraversalName = (
  state: MetaInfoState,
  { name }: SetNamePayload
): MetaInfoState => ({
  ...state,
  name,
});

const setSelectedView = (
  state: MetaInfoState,
  { selectedView }: SelectedViewState
) => ({
  ...state,
  viewName: selectedView?.value ?? ViewIds.NONE,
});

type SelectedViewWarningDependencies = {
  selectedView: ViewIds;
  labelFormatting: LabelFormattingInfo[];
  groupingRules: PerspectiveGroupingRule[];
};

const setSelectedViewWarning = (
  state: MetaInfoState,
  {
    selectedView,
    labelFormatting,
    groupingRules,
  }: SelectedViewWarningDependencies
) => {
  const viewSelectWarning = getViewSelectorWarningCode({
    viewId: selectedView,
    labelFormatting,
    groupingRules,
  });

  return {
    ...state,
    viewSelectWarning,
  };
};

const setInitialState = (
  state: MetaInfoState,
  { initialState }: SetInitialMetaInfoPayload
): MetaInfoState => {
  if (initialState === null) {
    return {
      ...state,
      initialState,
    };
  }

  return {
    ...state,
    initialState,
    name: initialState.name,
    description: initialState.description,
    viewName: initialState.viewName,
  };
};

const setTraversalDescription = (
  state: MetaInfoState,
  { description }: SetDescriptionPayload
) => ({
  ...state,
  description,
});

const stateWithSavableAttributes = (
  state: MetaInfoState,
  viewpointModeViewsById: ModeViews<ViewpointModeViewKey>['byIds']
): MetaInfoState => {
  const { name, viewName, description, initialState, folderId } = state;
  const savableAttributesCandidate = {
    name,
    viewName,
    description,
    _id: initialState?._id ?? null,
    _version: initialState?._version ?? null,
    folderId: folderId ?? undefined,
  };

  return {
    ...state,
    canSaveViewpoint: isTraversalUpdateOrSaveAttributes(
      savableAttributesCandidate,
      viewpointModeViewsById
    ),
    stateAsSavableAttributes: savableAttributesCandidate,
  };
};

const isTraversalUpdateOrSaveAttributes = (
  attributes: {
    name: string | null;
    description: string | null;
    viewName: ViewIds;
  },
  viewpointModeViewsById: ModeViews<ViewpointModeViewKey>['byIds']
) => {
  const { name, viewName } = attributes;
  return (
    Boolean(name?.trim()) && Object.hasOwn(viewpointModeViewsById, viewName)
  );
};

const resetMetaInfoState = () => getInitialMetaInfoState();

export const viewpointMetaInfoOperations = {
  getInitialMetaInfoState,
  processDependencies,
  setTraversalName,
  setInitialState,
  setSelectedView,
  setTraversalDescription,
  stateWithSavableAttributes,
  setSelectedViewWarning,
  resetMetaInfoState,
};
